import type { MatchResult } from "@/components/typeahead/types.ts";

/**
 * Extract the word (or partial multi-word phrase) currently being typed
 * at the given cursor position. Words are delimited by the start of input
 * or whitespace.
 *
 * For multi-word trigger matching, this also considers the preceding word
 * so that typing "catch u" can match "Catch up".
 */
export function extractCurrentWord(
  fullText: string,
  cursorPosition: number,
): { word: string; startIndex: number } {
  const textBeforeCursor = fullText.slice(0, cursorPosition);

  // Find the start of the current word (last space before cursor, or 0).
  const lastSpace = textBeforeCursor.lastIndexOf(" ");
  const currentWordStart = lastSpace + 1;
  const currentWord = textBeforeCursor.slice(currentWordStart);

  // Also grab the two-word phrase ending at cursor for multi-word triggers
  // like "Catch up", "Check in", "Find time", etc.
  const beforeLastSpace = lastSpace === -1 ? "" : textBeforeCursor.slice(0, lastSpace);
  const secondLastSpace = beforeLastSpace.lastIndexOf(" ");
  const twoWordStart = secondLastSpace + 1;
  const twoWordPhrase = textBeforeCursor.slice(twoWordStart);

  // Return the longer phrase if it contains a space (potential multi-word match).
  // The caller passes this to matchTriggers which checks both the full phrase
  // and individual words.
  if (twoWordPhrase.includes(" ") && twoWordPhrase.trim().length > 0) {
    return { word: twoWordPhrase, startIndex: twoWordStart };
  }

  return { word: currentWord, startIndex: currentWordStart };
}

/**
 * Common abbreviations that map to trigger words.
 * Checked before fuzzy matching so that well-known shorthands
 * always produce the expected result (e.g. "mtg" → Meet).
 */
const ABBREVIATION_MAP: Record<string, string[]> = {
  mtg: ["Meet"],
  appt: ["Schedule"],
};

/**
 * Match typed input against a list of trigger words.
 *
 * Strategy:
 * 1. **Prefix match** — typed characters match the beginning of a trigger word
 *    (case-insensitive). Score: 1.0.
 * 2. **Multi-word prefix** — for input containing spaces, check multi-word
 *    triggers. If no match, returns empty.
 * 3. **Abbreviation map** — common scheduling abbreviations (e.g. "mtg").
 * 4. **Fuzzy fallback** — character-subsequence matching with a 0.5 minimum
 *    confidence threshold. The first character of input must match the first
 *    character of the trigger to reduce false positives.
 *
 * @returns Matched trigger words sorted alphabetically for prefix matches,
 *          or by descending score for fuzzy matches.
 */
export function matchTriggers(input: string, triggerWords: readonly string[]): MatchResult[] {
  const normalized = input.toLowerCase().trim();
  if (normalized.length < 2) return [];

  // Phase 1: Prefix match
  const prefixMatches = triggerWords
    .filter((w) => w.toLowerCase().startsWith(normalized))
    .map((w): MatchResult => ({ word: w, score: 1.0, matchType: "prefix" }));

  if (prefixMatches.length > 0) {
    return prefixMatches.sort((a, b) => a.word.localeCompare(b.word));
  }

  // Phase 2: Multi-word prefix match ("catch u" → "Catch up")
  if (normalized.includes(" ")) {
    const multiWordPrefixMatches = triggerWords
      .filter((w) => {
        const lower = w.toLowerCase();
        return lower.includes(" ") && lower.startsWith(normalized);
      })
      .map(
        (w): MatchResult => ({
          word: w,
          score: 1.0,
          matchType: "prefix",
        }),
      );

    if (multiWordPrefixMatches.length > 0) {
      return multiWordPrefixMatches.sort((a, b) => a.word.localeCompare(b.word));
    }

    return [];
  }

  // Phase 3: Abbreviation map
  const abbreviationMatches = ABBREVIATION_MAP[normalized];
  if (abbreviationMatches) {
    return abbreviationMatches
      .filter((w) => triggerWords.includes(w))
      .map((w): MatchResult => ({ word: w, score: 0.9, matchType: "fuzzy" }));
  }

  // Phase 4: Fuzzy fallback (subsequence matching)
  // Require the first character to match to reduce false positives
  // (e.g. "the" should NOT match "Get together")
  const firstChar = normalized[0];
  const fuzzyMatches = triggerWords
    .filter((w) => w.toLowerCase()[0] === firstChar)
    .map(
      (w): MatchResult => ({
        word: w,
        score: fuzzyScore(normalized, w.toLowerCase()),
        matchType: "fuzzy",
      }),
    )
    .filter((m) => m.score > 0.5)
    .sort((a, b) => b.score - a.score);

  return fuzzyMatches;
}

/**
 * Compute a fuzzy match score between `input` and `target` using
 * character-subsequence matching with a bonus for consecutive characters.
 *
 * @returns A normalized score between 0 and 1. Returns 0 if not all
 *          input characters appear as a subsequence in the target.
 */
function fuzzyScore(input: string, target: string): number {
  let inputIdx = 0;
  let consecutive = 0;
  let score = 0;

  for (let i = 0; i < target.length && inputIdx < input.length; i++) {
    if (target[i] === input[inputIdx]) {
      inputIdx++;
      consecutive++;
      score += consecutive; // bonus for consecutive matches
    } else {
      consecutive = 0;
    }
  }

  // Not all input characters were found in the target
  if (inputIdx < input.length) return 0;

  // Normalize: maximum possible score is sum(1..n) = n*(n+1)/2
  const maxScore = (input.length * (input.length + 1)) / 2;
  return score / maxScore;
}
